import { useEffect, useMemo, useRef } from "react"
import { useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Banner } from "@/components/ui/banner"
import { AgentAvailabilityIndicator } from "@/modules/patients/agenda/teleconsultation/booking/date-slot-selector/agent-availability-indicator"
import { InformBanner } from "@/modules/patients/agenda/teleconsultation/booking/date-slot-selector/inform-banner"
import { SlotDate } from "@/modules/patients/agenda/teleconsultation/booking/date-slot-selector/slot-date"
import { SlotDatePicker } from "@/modules/patients/agenda/teleconsultation/booking/date-slot-selector/slot-date-picker"
import {
  AgentUserNameOrGenderInput,
  AvailableCapacitiesInput,
  useFetchAvailableCapacities,
} from "@/modules/patients/agenda/teleconsultation/booking/hooks/useFetchAvailableCapacities"
import { TeleconsultationForm } from "@/modules/patients/agenda/teleconsultation/booking/teleconsultation-schema"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"

type DateSlotSelectorProps = {
  isEditMode?: boolean
}

const DateSlotSelector = ({ isEditMode }: DateSlotSelectorProps) => {
  const {
    control,
    formState: { errors },
    setValue,
    getValues,
  } = useFormContext<TeleconsultationForm>()
  const { t } = useTranslation()

  const appointmentTypeWatch = useWatch({ control, name: "appointmentType" })
  const serviceWatch = useWatch({ control, name: "service" })
  const specialtyWatch = useWatch({ control, name: "specialty" })
  const languageWatch = useWatch({ control, name: "language" })
  const flexibilityWatch = useWatch({ control, name: "flexibility" })
  const medicalUrgencyWatch = useWatch({ control, name: "urgency" })
  const genderWatch = useWatch({ control, name: "gender" })
  const agentUserNameWatch = useWatch({ control, name: "agentUserName" })
  const appointmentIdWatch = useWatch({ control, name: "id" })
  const channelWatch = useWatch({ control, name: "channel" })

  const countryCode = useCountryParam()

  const shouldSelectExistingSlotInTheFirstTimeOpeningEditMode = useRef(false)
  const timeSlotBannerRef = useRef<HTMLDivElement>(null)

  const filters = useMemo((): AvailableCapacitiesInput => {
    let agentUserNameOrGender: AgentUserNameOrGenderInput | undefined

    if (agentUserNameWatch) {
      agentUserNameOrGender = {
        agentUserName: agentUserNameWatch,
      }
    } else if (genderWatch && genderWatch !== "UNKNOWN") {
      agentUserNameOrGender = {
        agentGender: genderWatch,
      }
    }

    return {
      appointmentType: appointmentTypeWatch,
      licensedInCountry: countryCode,
      service: serviceWatch,
      specialty: specialtyWatch,
      language: languageWatch,
      agentUserNameOrGender,
      rescheduledAppointmentId: appointmentIdWatch,
      channel: channelWatch,
    }
  }, [
    agentUserNameWatch,
    appointmentIdWatch,
    appointmentTypeWatch,
    channelWatch,
    countryCode,
    genderWatch,
    languageWatch,
    serviceWatch,
    specialtyWatch,
  ])

  const isEmergency =
    medicalUrgencyWatch !== "NORMAL" || serviceWatch === "EMERGENCY_LINE" || serviceWatch === "KIDS_LINE"

  const { availableDates, data, debounceSearch, hasAvailableSlot, isLoading } = useFetchAvailableCapacities(
    filters,
    getValues("date"),
    isEmergency,
  )
  const availableSlots = data?.availableSlotsCapacity.result

  const showTimeSlotBannerWarning = () => timeSlotBannerRef.current?.removeAttribute("style")

  useEffect(() => {
    if (!availableSlots) return

    const firstAvailableSlot = availableSlots.find((s) => s.isAvailable || s.allowPriorityBooking)

    if (isEditMode && !shouldSelectExistingSlotInTheFirstTimeOpeningEditMode.current) {
      const canSelectedSlotIdWatch = availableSlots.find(
        (s) => getValues("slotId") === s.slotId && (s.isAvailable || s.allowPriorityBooking),
      )
      if (!firstAvailableSlot && getValues("urgency") === "NORMAL") {
        showTimeSlotBannerWarning()
      } else if (firstAvailableSlot && !canSelectedSlotIdWatch) {
        setValue("slotId", firstAvailableSlot.slotId)

        // handle show warning banner
        // no need to useState to update value
        if (getValues("urgency") === "NORMAL") {
          showTimeSlotBannerWarning()
        }
      }

      shouldSelectExistingSlotInTheFirstTimeOpeningEditMode.current = true
    } else if (firstAvailableSlot) {
      setValue("slotId", firstAvailableSlot.slotId)
    } else {
      setValue("slotId", null)
    }
  }, [availableSlots, getValues, isEditMode, setValue])

  return (
    <div className="grid grid-rows-1 gap-8 py-4">
      {!isEmergency && (
        <SlotDatePicker {...availableDates} hasAvailableSlot={hasAvailableSlot} onChange={debounceSearch} />
      )}
      {!isEmergency && agentUserNameWatch && (
        <AgentAvailabilityIndicator
          filters={filters}
          onSelectDate={(date) => {
            setValue("date", date, { shouldDirty: true })
            debounceSearch(date)
          }}
        />
      )}
      <InformBanner flexibility={flexibilityWatch} medicalUrgency={medicalUrgencyWatch} />
      {!hasAvailableSlot && !isEmergency && !isLoading && (
        <Banner type="inline" variant="warning" data-testid="no-capacity-available-message">
          {t("patient.agenda.urgency.inform.noCapacityAvailable")}
        </Banner>
      )}
      {!isEmergency && (
        <>
          <Banner ref={timeSlotBannerRef} type="inline" className="-mt-4" variant="warning" style={{ display: "none" }}>
            {t("patient.agenda.timeSlotWarning")}
          </Banner>
          {errors.slotId?.message && (
            <Banner
              type="inline"
              variant="critical"
              data-testid="patient-urgency-flexibility-message"
              className="-mt-4">
              {t(errors.slotId?.message)}
            </Banner>
          )}
          <SlotDate availableSlotsCapacity={data ?? null} isLoading={isLoading} />
        </>
      )}
    </div>
  )
}

export { DateSlotSelector }
